import React, { useContext, memo, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Accordion, useAccordionButton, AccordionContext, Tooltip, OverlayTrigger } from 'react-bootstrap'
import SidebarMenu from './SidebarMenu'
import { AuthContext } from '@/context/auth-context'
import { SettingsContext } from '@/context/settings-context'
import { ChevronLeftIcon, DashbordIcon, ListIcon, PlusIcon, VolumeIcon } from '@/components/icons/new'

function useClickOutside(ref, handler) {
    useEffect(() => {
        const listener = (event) => {
            if (event.target.closest('.sidebar-toggle')) return

            if (!ref.current || ref.current.contains(event.target)) return

            const path = window.location.pathname
            const autoCollapse = path.startsWith('/campaigns/media-buying')
            if (autoCollapse) {
                // click outside only toggles when in media buying.
                handler(event)
            }
        }
        document.addEventListener('mousedown', listener)
        return () => document.removeEventListener('mousedown', listener)
    }, [ref, handler])
}

const CustomToggle = ({ children, eventKey, onClick }) => {
    const { activeEventKey } = useContext(AccordionContext)
    const decoratedOnClick = useAccordionButton(eventKey, (active) => onClick({ state: !active, eventKey: eventKey }))
    const isCurrentEventKey = activeEventKey === eventKey

    return (
        <Link
            to="#"
            aria-expanded={isCurrentEventKey ? 'true' : 'false'}
            className="nav-link"
            role="button"
            onClick={(e) => {
                decoratedOnClick(isCurrentEventKey)
            }}
        >
            {children}
        </Link>
    )
}

function getDefaultActiveKey(pathname) {
    if (pathname === '/campaigns' || pathname === '/campaigns/new') return 'sidebar-campaign'
    return undefined
}

const VerticalNav = memo(({ setToggleMini }) => {
    const location = useLocation()
    const navRef = useRef(null)
    const { onboarding } = useContext(AuthContext)
    const { sideMenuCollapsed } = useContext(SettingsContext)
    // collapse on outside clicks
    useClickOutside(navRef, (ev) => {
        setTimeout(() => setToggleMini(ev, true), 400)
    })

    const isDisabled = onboarding === true
    const defaultActiveKey = getDefaultActiveKey(location.pathname)

    return (
        <div ref={navRef} style={isDisabled ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
            <Accordion as="ul" className="navbar-nav iq-main-menu" defaultActiveKey={defaultActiveKey}>
                <li className={`nav-item ${location.pathname.startsWith('/dashboard') ? 'active' : ''}`}>
                    <Link to="/dashboard/overview" className="nav-link">
                        <OverlayTrigger placement="right" overlay={<Tooltip>Dashboard</Tooltip>}>
                            <i className="icon text-gray-200">
                                <DashbordIcon />
                            </i>
                        </OverlayTrigger>
                        <span className="item-name">Dashboard</span>
                    </Link>
                </li>

                <Accordion.Item
                    as="li"
                    eventKey="sidebar-campaign"
                    bsPrefix={`nav-item ${location.pathname === '/campaigns' || location.pathname === '/campaigns/new' ? 'active' : ''}`}
                >
                    <CustomToggle eventKey="sidebar-campaign" onClick={(activeKey) => {}}>
                        <OverlayTrigger placement="right" overlay={<Tooltip>Licenses</Tooltip>}>
                            <i className="icon text-gray-200">
                                <VolumeIcon />
                            </i>
                        </OverlayTrigger>
                        <span className="item-name">Licenses</span>
                        {sideMenuCollapsed !== 'true' && (
                            <i className="right-icon text-gray-200">
                                <ChevronLeftIcon />
                            </i>
                        )}
                    </CustomToggle>
                    <Accordion.Collapse eventKey="sidebar-campaign">
                        <ul className="sub-nav">
                            <SidebarMenu
                                isTag="false"
                                pathname="/campaigns"
                                title="Licenses List"
                                miniIcon={<ListIcon />}
                            >
                                <i className="icon text-gray-200">
                                    <ListIcon />
                                </i>
                            </SidebarMenu>
                            <SidebarMenu
                                isIcon="true"
                                isTag="false"
                                pathname="/campaigns/new"
                                title="Add License"
                                miniIcon={<PlusIcon />}
                            >
                                <i className="icon text-gray-200">
                                    <PlusIcon />
                                </i>
                            </SidebarMenu>
                        </ul>
                    </Accordion.Collapse>
                </Accordion.Item>

            </Accordion>
        </div>
    )
})

VerticalNav.displayName = 'VerticalNav'
export default VerticalNav
